import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18N } from "common/hooks";
import { placesIndexQueryOptions } from "common/hooks/places/placesQueryOptions";
import { Footer } from "@/components/Footer";
import { CityLinkCard } from "@/components/Places/CityLinkCard";
import { PlacesCitySkeleton, PlacesIndexSkeleton } from "@/components/Places/PlacesSkeletons";
import { PlacesUnavailable } from "@/components/Places/PlacesUnavailable";
import { getWikipediaImages } from "@/features/onboarding/utils/wikiImages";
import { buildPlacesIndexHead } from "@/lib/placesPage";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export const Route = createFileRoute("/places/")({
  ssr: true,
  component: PlacesIndex,
  errorComponent: PlacesUnavailable,
  // In-app navigations wait on the loader below; a server render never shows this.
  pendingComponent: PlacesIndexPending,
  pendingMs: 200,
  loader: async ({ context }) => {
    const canonicalOrigin =
      (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "";
    // A PlacesUnavailableError propagates: the route errors with a 5xx, never an empty index page.
    await context.queryClient.ensureQueryData(placesIndexQueryOptions());
    return { canonicalOrigin };
  },
  head: ({ loaderData }) =>
    buildPlacesIndexHead(loaderData?.canonicalOrigin ?? "", isProduction),
});

function countryHeadingId(country: string): string {
  return `places-country-${country.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

/** True once `active` has held for `delayMs`, so a cached, instant navigation never flashes a skeleton. */
function useShownAfter(active: boolean, delayMs: number): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return shown;
}

/** A skeleton page with the wait announced once for a screen reader. */
function PlacesLoading({ skeleton }: { skeleton: ReactNode }) {
  const i18n = useI18N();
  return (
    <>
      <p role="status" className="sr-only">
        {i18n.get("loading")}
      </p>
      <main id="main" aria-busy="true" className="w-full bg-bg-brand-secondary-default">
        {skeleton}
      </main>
    </>
  );
}

function PlacesIndexPending() {
  return <PlacesLoading skeleton={<PlacesIndexSkeleton />} />;
}

function PlacesIndex() {
  const i18n = useI18N();
  const { data } = useQuery(placesIndexQueryOptions());
  const countries = useMemo(() => data?.data.countries ?? [], [data]);
  // The city route keeps no pending screen (it would blank page and filter changes), so the
  // index draws the city skeleton while a city it linked to loads.
  const openingCity = useShownAfter(
    useRouterState({ select: (state) => state.isLoading && /^\/places\/[^/]+$/.test(state.location.pathname) }),
    200,
  );

  // Photos load in the browser so the server HTML never waits on Wikipedia.
  const [images, setImages] = useState<Record<string, string | null> | null>(null);
  const cityNames = useMemo(
    () => countries.flatMap((country) => country.cities.map((city) => city.name)),
    [countries],
  );
  useEffect(() => {
    let active = true;
    if (cityNames.length > 0) {
      getWikipediaImages(cityNames, 200)
        .then((result) => {
          if (active) setImages(result);
        })
        .catch(() => {
          if (active) setImages({});
        });
    }
    return () => {
      active = false;
    };
  }, [cityNames]);

  if (openingCity) {
    return (
      <>
        <PlacesLoading skeleton={<PlacesCitySkeleton />} />
        <Footer />
      </>
    );
  }

  return (
    <>
      <main id="main" className="w-full bg-bg-brand-secondary-default">
        <div className="mx-auto flex max-w-[1160px] flex-col gap-8 px-4 py-6 md:py-10">
          <h1 className="headline-l-bold text-fg-neutral-strong">{i18n.get("placesIndexHeading")}</h1>
          {countries.map((country) => (
            <section key={country.country} aria-labelledby={countryHeadingId(country.country)}>
              <h2 id={countryHeadingId(country.country)} className="title-m-bold text-fg-neutral-strong mb-3">
                {country.country}
              </h2>
              <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
                {country.cities.map((city) => (
                  <li key={city.slug}>
                    <CityLinkCard city={city} imageUrl={images ? (images[city.name.trim()] ?? null) : undefined} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
